<template>
    <div id="app">
        <Navigation
            v-if="showUserNavigation"
            ref="UserNavigation"
        />

        <alert-alert />
        <main
            id="main"
            tabindex="-1"
            class="router-wrapper"
            :class="{
                'router-wrapper--hide-border': hideBorder,
                'router-wrapper--no-nav': !showUserNavigation,
                'staff-school-view-enabled': staffSchoolViewEnabled
            }"
        >
            <div
                class="router"
                :class="{
                    'router-logged-out': !showUserNavigation
                }"
                ref="Router"
            >
                <BackgroundOperation
                    v-if="showUserNavigation"
                    v-for="op in rootStore.backgroundOperations"
                    :key="op.id"
                    :backgroundOperation="op"
                />
                <!-- Binding a key to the full path will remount a view if the detail endpoint changes-->
                <router-view
                    :key="$route.fullPath"
                    @scrollToTop="scrollToTop"
                />
            </div>
        </main>
        <ModalWelcome />
        <StudentChatHistoryDrawer
            class="app-drawer"
            :class="{
                'staff-school-view-enabled': staffSchoolViewEnabled
            }"
            v-if="studentChatHistoryDrawerData.isOpen"
            :studentId="studentChatHistoryDrawerData.studentId"
            :scrollToInterventionTemplateId="
                studentChatHistoryDrawerData.scrollToInterventionTemplateId
            "
            @close="clearStudentChatHistoryDrawerData"
        />
    </div>
</template>

<script>
import Navigation from '@/features/Navigation/Navigation.vue';
import ModalWelcome from '@/views/authentication/ModalWelcome';
import Auth from '@/services/auth';
import { debounce } from '@/services/utils';
import BackgroundOperation from '@/components-deprecated/BackgroundOperation';
import { useIdle } from '@vueuse/core';
import 'primeicons/primeicons.css';
import StudentChatHistoryDrawer from '@/features/StudentChatHistoryDrawer/StudentChatHistoryDrawer.vue';
import { useRootStore } from '@/stores';
const ONE_MIN = 1000 * 60;
const IDLE_LOGOUT_TIME = ONE_MIN * 90;
const IDLE_WARNING_TIME = IDLE_LOGOUT_TIME - ONE_MIN;

export default {
    name: 'App',
    components: {
        StudentChatHistoryDrawer,
        Navigation,
        ModalWelcome,
        BackgroundOperation
    },
    setup() {
        const { lastActive } = useIdle(0);
        const rootStore = useRootStore();

        return {
            lastActive,
            rootStore
        };
    },
    data() {
        return {
            currentRouteName: this.$router.currentRoute.name,
            removeRouterListener: null,
            logoutWarned: false,
            initialStudentChatHistoryDrawerData: {
                isOpen: false,
                studentId: null,
                scrollToInterventionTemplateId: null
            },
            studentChatHistoryDrawerData: {
                ...this.initialStudentChatHistoryDrawerData
            }
        };
    },
    computed: {
        userIsLoggedIn() {
            return this.rootStore.userIsLoggedIn;
        },
        isStaff() {
            return this.rootStore.user.isStaff;
        },
        showUserNavigation() {
            const isHideNavbarPage = [
                'Login',
                'Registration',
                'ForgotPassword',
                'ForgotPasswordSuccess',
                'PasswordResetConfirm',
                'SurveyIntro',
                'SurveyContent',
                'SurveyEnd',
                'MagicLink',
                'StaffEnterSchoolPage'
            ].includes(this.currentRouteName);
            return !isHideNavbarPage && this.userIsLoggedIn;
        },
        hideBorder() {
            return [
                'Login',
                'Registration',
                'ForgotPassword',
                'ForgotPasswordSuccess',
                'PasswordResetConfirm',
                'SurveyIntro',
                'SurveyContent',
                'SurveyEnd',
                'StaffEnterSchoolPage'
            ].includes(this.currentRouteName);
        },
        hideTransition() {
            return [
                'Login',
                'Registration',
                'ForgotPassword',
                'ForgotPasswordSuccess',
                'PasswordResetConfirm',
                'InviteCoAdmins'
            ].includes(this.currentRouteName);
        },
        staffSchoolViewEnabled() {
            return this.rootStore.staffSchoolViewEnabled;
        }
    },
    async created() {
        // listen to route changes
        this.removeRouterListener = this.$router.afterEach(to => {
            // Set page title:
            document.title = to.meta && to.meta.title ? `EdSights - ${to.meta.title}` : 'EdSights';
            // Track route name, so we can hide/show navigation:
            this.currentRouteName = to.name;
        });
        if (this.userIsLoggedIn) {
            this.rootStore.refreshUser();
        }

        this.setLastActive = debounce(this.setLastActive, 1000);
    },
    mounted() {
        this.watchActivity();
    },
    beforeUnmount() {
        this.removeRouterListener();
        clearInterval(this.activityWatcherId);
    },
    methods: {
        scrollToTop() {
            this.$refs.Router.scrollTo(0, 0);
        },
        setLastActive(timestamp) {
            localStorage.setItem('lastActive', timestamp);
        },
        watchActivity() {
            this.activityWatcherId = setInterval(() => {
                if (!Auth.isLoggedIn()) {
                    return;
                }
                const lastActiveTimestamp = localStorage.getItem('lastActive');
                const currentTimestamp = new Date().getTime();
                if (
                    currentTimestamp - lastActiveTimestamp >= IDLE_WARNING_TIME &&
                    !this.logoutWarned
                ) {
                    this.logoutWarned = true;
                    this.$Alert.alert({
                        type: 'warning',
                        message:
                            'You will be logged out in 1 minute due to inactivity. Any mouse or keyboard activity keeps you signed in.',
                        timeout: 20000
                    });
                }
                if (currentTimestamp - lastActiveTimestamp >= IDLE_LOGOUT_TIME) {
                    Auth.logout();
                    this.$router.push({ name: 'Login' });
                    this.$Alert.alert({
                        type: 'error',
                        message: 'You have been logged out due to inactivity.',
                        timeout: 20000
                    });
                }
            }, 15000);
        },
        openStudentChatHistoryDrawer({ studentId, scrollToInterventionTemplateId }) {
            // Ensure any prior data is cleared:
            this.clearStudentChatHistoryDrawerData();

            // Open the drawer and set the student:
            this.studentChatHistoryDrawerData.isOpen = true;
            this.studentChatHistoryDrawerData.studentId = studentId;
            this.studentChatHistoryDrawerData.scrollToInterventionTemplateId = scrollToInterventionTemplateId;
        },
        clearStudentChatHistoryDrawerData() {
            this.studentChatHistoryDrawerData = { ...this.initialStudentChatHistoryDrawerData };
        }
    },
    watch: {
        lastActive(lastActive) {
            if (lastActive) {
                this.logoutWarned = false;
                this.setLastActive(lastActive);
            }
        },
        // set lastActive upon login to prevent auto logout
        userIsLoggedIn(hasUserLoggedIn) {
            if (hasUserLoggedIn) {
                this.setLastActive(Date.now());
            }
        }
    },
    provide() {
        return {
            openStudentChatHistoryDrawer: this.openStudentChatHistoryDrawer
        };
    }
};
</script>

<style lang="scss">
@import './styles/variables';
@import './styles/base';

#app {
    // Important for full-height flexbox layout!
    height: 100%;
    overflow-y: hidden;
}

.router-wrapper {
    background-color: #f5f6fa;
    height: calc(100vh - #{$navigation-height});
    overflow-y: hidden;
    margin-top: $navigation-height;

    &.staff-school-view-enabled {
        $content-height: calc(100vh - #{$staff-total-navigation-height});
        height: $content-height;
        margin-top: $staff-total-navigation-height;

        .router {
            height: $content-height;
        }
    }

    &--no-nav {
        margin-top: 0;
    }

    &--hide-border {
        padding: 0;
        height: 100vh;

        .router {
            height: 100vh;
        }
    }
}

.router {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-x: hidden;
    // Important for full-height flexbox layout!
    margin-left: 0;
    background-color: $white;
    height: calc(100vh - #{$navigation-height});
    box-shadow: 0 0 10px #00000021;

    &-logged-out {
        padding-top: 0;
    }
}

@media screen and (min-width: 728px) {
    .router {
        &.router-logged-out {
            flex-direction: row;
            margin-left: 0;
        }
    }
}

// Any app-level drawers must have this class applied
// to open underneath the navigation.
.app-drawer {
    height: calc(100vh - #{$navigation-height}) !important;
    margin-top: auto;

    &.staff-school-view-enabled {
        height: calc(100vh - #{$staff-total-navigation-height}) !important;
    }
}
</style>
